<template>
  <!-- Remplacement de font-light par font-sans et couleurs de fond adaptatives -->
  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200 font-sans"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <!-- En-tête principal épuré -->
    <div class="flex justify-between items-center border-b pb-5 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-0.5">
        <h1 class="text-xl font-semibold tracking-tight transition-colors duration-200"
          :class="isDark ? 'text-white' : 'text-zinc-900'">
          {{ isAccompagnement ? 'Rendez-vous d\'Accompagnement' : 'Nouveau Rendez-vous' }}
        </h1>
        <p class="text-xs font-medium transition-colors duration-200"
          :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          {{ isAccompagnement
            ? 'Planifiez votre session personnalisée pour découvrir toutes les fonctionnalités de BookiPro.'
            : 'Planifiez une nouvelle prestation, attribuez un collaborateur et configurez l\'acompte.'
          }}
        </p>
      </div>
      <div class="flex gap-2.5">
        <button @click="resetForm" class="px-4 py-2 rounded-xl text-xs font-medium border transition-all cursor-pointer focus:outline-none"
          :class="isDark 
            ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-400 hover:text-white' 
            : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-600 hover:text-zinc-900 shadow-sm'">
          Réinitialiser
        </button>
        <button @click="createAppointment" :disabled="isSubmitting" class="px-5 py-2.5 rounded-xl text-xs font-semibold transition-all shadow-sm active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus:outline-none"
          :class="isDark 
            ? 'bg-white hover:bg-zinc-200 text-black' 
            : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
          {{ isSubmitting ? 'Enregistrement...' : 'Enregistrer' }}
        </button>
      </div>
    </div>

    <!-- État de chargement -->
    <div v-if="isLoading" class="flex justify-center items-center py-20">
      <div class="text-sm font-medium animate-pulse" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
        Chargement des données...
      </div>
    </div>

    <!-- Contenu du Formulaire -->
    <div v-else class="grid lg:grid-cols-3 gap-6 items-start w-full">

      <!-- Colonne de gauche : Formulaires -->
      <div class="lg:col-span-2 space-y-6">

        <!-- Étape 1 : Choix du Client -->
        <div class="border rounded-2xl p-6 space-y-4 transition-colors duration-200"
          :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200/80 shadow-sm'">
          <div class="border-b pb-3 flex justify-between items-center transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-zinc-100'">
            <div>
              <h2 class="text-sm font-semibold tracking-wide transition-colors duration-200"
                :class="isDark ? 'text-white' : 'text-zinc-900'">1. Sélection du Client</h2>
              <p class="text-[11px] font-medium transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Associez ce rendez-vous à un client existant ou créez une fiche éphémère.</p>
            </div>
            <button @click="router.push('/clients/new')" class="text-xs font-semibold hover:underline cursor-pointer focus:outline-none transition-colors"
              :class="isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-600 hover:text-zinc-900'">
              + Nouvelle fiche
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-1.5 md:col-span-2">
              <label class="block text-[11px] font-semibold transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Rechercher un client</label>
              <div class="relative">
                <input
                  v-model="form.clientSearch"
                  @input="searchClients"
                  type="text"
                  placeholder="Nom, email ou numéro de mobile..."
                  class="w-full px-4 py-2.5 rounded-xl border text-xs transition-all focus:outline-none"
                  :class="isDark 
                    ? 'bg-zinc-900/40 border-zinc-900 text-white placeholder-zinc-700 focus:border-zinc-700 focus:bg-zinc-900/80' 
                    : 'bg-zinc-50 border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:border-zinc-300 focus:bg-white'"
                />
                <span class="absolute right-4 top-3 text-xs transition-opacity duration-200" :class="isDark ? 'opacity-40' : 'opacity-60'">🔍</span>
              </div>
              
              <!-- Liste des résultats de recherche -->
              <div v-if="clientSearchResults.length > 0" class="mt-2 max-h-48 overflow-y-auto rounded-xl border shadow-lg transition-colors duration-200"
                :class="isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'">
                <div
                  v-for="client in clientSearchResults"
                  :key="client.clientId"
                  @click="selectClient(client)"
                  class="px-4 py-2.5 cursor-pointer text-xs border-b transition-colors"
                  :class="isDark ? 'border-zinc-950 hover:bg-zinc-800' : 'border-zinc-100 hover:bg-zinc-50'"
                >
                  <div class="font-semibold transition-colors" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ client.nom }} {{ client.prenom }}</div>
                  <div class="text-[10px] font-medium mt-0.5" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">{{ client.email }} • {{ client.telephone }}</div>
                </div>
              </div>
              
              <!-- Affichage du client sélectionné -->
              <div v-if="selectedClient" class="mt-2 p-3 rounded-xl border flex justify-between items-center transition-all"
                :class="isDark ? 'bg-emerald-950/20 border-emerald-900/40' : 'bg-emerald-50/60 border-emerald-200/60'">
                <div class="text-xs">
                  <div class="font-semibold" :class="isDark ? 'text-emerald-400' : 'text-emerald-800'">{{ selectedClient.nom }} {{ selectedClient.prenom }}</div>
                  <div class="text-[10px] font-medium mt-0.5" :class="isDark ? 'text-zinc-500' : 'text-emerald-600/80'">{{ selectedClient.email }}</div>
                </div>
                <button @click="clearSelectedClient" class="text-xs transition-colors focus:outline-none cursor-pointer"
                  :class="isDark ? 'text-zinc-500 hover:text-red-400' : 'text-zinc-400 hover:text-red-600'">✕</button>
              </div>
            </div>

            <!-- Formulaire manuel si nouveau client -->
            <div v-if="!selectedClient" class="space-y-1.5">
              <label class="block text-[11px] font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Nom complet</label>
              <input v-model="form.nomComplet" type="text" placeholder="Ex: Marie Durant" class="w-full px-4 py-2.5 rounded-xl border text-xs focus:outline-none transition-all"
                :class="isDark ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700' : 'border-zinc-200 bg-zinc-50 text-zinc-900 focus:border-zinc-300 focus:bg-white'" />
            </div>

            <div v-if="!selectedClient" class="space-y-1.5">
              <label class="block text-[11px] font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Adresse e-mail</label>
              <input v-model="form.email" type="email" placeholder="Ex: marie@exemple.com" class="w-full px-4 py-2.5 rounded-xl border text-xs focus:outline-none transition-all"
                :class="isDark ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700' : 'border-zinc-200 bg-zinc-50 text-zinc-900 focus:border-zinc-300 focus:bg-white'" />
            </div>

            <div v-if="!selectedClient" class="space-y-1.5 md:col-span-2">
              <label class="block text-[11px] font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Numéro de téléphone</label>
              <input v-model="form.telephone" type="tel" placeholder="Ex: 06 12 34 56 78" class="w-full px-4 py-2.5 rounded-xl border text-xs focus:outline-none transition-all"
                :class="isDark ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700' : 'border-zinc-200 bg-zinc-50 text-zinc-900 focus:border-zinc-300 focus:bg-white'" />
            </div>
          </div>
        </div>

        <!-- Étape 2 : Choix des Prestations -->
        <div class="border rounded-2xl p-6 space-y-4 transition-colors duration-200"
          :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200/80 shadow-sm'">
          <div class="border-b pb-3 transition-colors duration-200" :class="isDark ? 'border-zinc-900' : 'border-zinc-100'">
            <h2 class="text-sm font-semibold tracking-wide tracking-wide transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-zinc-900'">2. Choix des Prestations</h2>
            <p class="text-[11px] font-medium transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Sélectionnez un ou plusieurs soins pour configurer le rendez-vous.</p>
          </div>

          <div class="space-y-3">
            <div class="flex justify-between items-center">
              <label class="block text-[11px] font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Menu des soins disponibles</label>
              <p class="text-[10px] italic" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Cliquez pour sélectionner/désélectionner
              </p>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-48 overflow-y-auto pr-1 no-scrollbar">
              <div
                v-for="service in catalog"
                :key="service._id"
                @click="toggleService(service)"
                class="p-3 rounded-xl border cursor-pointer flex items-center justify-between transition-all"
                :class="isSelected(service)
                  ? (isDark ? 'border-zinc-600 bg-zinc-900 text-white' : 'border-zinc-900 bg-zinc-900 text-white')
                  : (isDark ? 'border-zinc-900 bg-zinc-900/40 hover:bg-zinc-900/80' : 'border-zinc-200 bg-zinc-50 hover:bg-zinc-100/80 text-zinc-700')"
              >
                <div class="text-left min-w-0 pr-2">
                  <p class="text-xs font-semibold truncate">{{ service.nom }}</p>
                  <p class="text-[11px] font-medium mt-0.5" :class="isSelected(service) ? 'text-zinc-400' : 'text-zinc-400'">
                    {{ service.duree }} min — {{ service.prix }} €
                  </p>
                </div>
                <div class="w-4 h-4 rounded-md border flex items-center justify-center text-[9px] font-bold transition-colors" 
                  :class="[isSelected(service) ? 'bg-white border-white text-black' : (isDark ? 'border-zinc-800' : 'border-zinc-300 bg-white')]">
                  <span v-if="isSelected(service)">✓</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Étape 3 : Date, Heure et Coiffeur -->
        <div class="border rounded-2xl p-6 space-y-4 transition-colors duration-200"
          :class="isDark ? 'bg-zinc-900/20 border-zinc-900' : 'bg-white border-zinc-200/80 shadow-sm'">
          <div class="border-b pb-3 transition-colors duration-200" :class="isDark ? 'border-zinc-900' : 'border-zinc-100'">
            <h2 class="text-sm font-semibold tracking-wide transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-zinc-900'">3. Date & Collaborateur</h2>
            <p class="text-[11px] font-medium transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Définissez le jour, l'heure du créneau ainsi que le membre de l'équipe disponible.</p>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div class="space-y-1.5">
              <label class="block text-[11px] font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Collaborateur</label>
              <select v-model="form.employeId" class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium transition-all appearance-none cursor-pointer focus:outline-none"
                :class="isDark ? 'border-zinc-900 bg-zinc-900/40 text-zinc-300 focus:border-zinc-700 focus:bg-zinc-900/80' : 'border-zinc-200 bg-zinc-50 text-zinc-700 focus:border-zinc-300 focus:bg-white shadow-sm'">
                <option value="">Choisir un membre...</option>
                <option v-for="emp in staff" :key="emp._id" :value="emp._id">{{ emp.prenom }} {{ emp.nom }}</option>
              </select>
            </div>

            <div class="space-y-1.5">
              <label class="block text-[11px] font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Date</label>
              <input v-model="form.date" type="date" class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium transition-all focus:outline-none"
                :class="isDark ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80' : 'border-zinc-200 bg-zinc-50 text-zinc-700 focus:border-zinc-300 focus:bg-white shadow-sm'" />
            </div>

            <div class="space-y-1.5">
              <label class="block text-[11px] font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Heure de début</label>
              <input v-model="form.startTime" type="time" class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium transition-all focus:outline-none"
                :class="isDark ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80' : 'border-zinc-200 bg-zinc-50 text-zinc-700 focus:border-zinc-300 focus:bg-white shadow-sm'" />
            </div>
          </div>
        </div>

      </div>

      <!-- Colonnes de droite : Panneau Récapitulatif -->
      <div class="w-full space-y-6">

        <div class="border rounded-2xl p-5 sticky top-6 space-y-6 transition-all duration-200"
          :class="isDark ? 'bg-zinc-900/30 border-zinc-900 shadow-2xl shadow-black/40' : 'bg-white border-zinc-200/80 shadow-md'">
          <div class="border-b pb-3 transition-colors duration-200" :class="isDark ? 'border-zinc-900' : 'border-zinc-100'">
            <h2 class="text-xs font-bold uppercase tracking-wider transition-colors duration-200"
              :class="isDark ? 'text-zinc-400' : 'text-zinc-500'">Récapitulatif du RDV</h2>
          </div>

          <div class="space-y-4 text-xs font-medium">
            <div v-if="selectedServices.length === 0" class="text-center py-6 text-zinc-400 font-normal italic">
              Aucune prestation sélectionnée.
            </div>

            <!-- Liste des prestations choisies -->
            <div v-else class="space-y-2 max-h-36 overflow-y-auto no-scrollbar pr-1">
              <div v-for="s in selectedServices" :key="s._id" class="flex justify-between items-center transition-colors duration-200"
                :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
                <span class="truncate pr-4 font-normal">{{ s.nom }}</span>
                <span class="font-semibold shrink-0">{{ s.prix }} €</span>
              </div>
            </div>

            <!-- Totaux et détails financiers -->
            <div class="border-t pt-4 space-y-2.5 transition-colors duration-200" :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100'">
              <div class="flex justify-between items-center text-zinc-400">
                <span class="font-normal">Durée totale</span>
                <span class="font-semibold transition-colors duration-200" :class="isDark ? 'text-zinc-200' : 'text-zinc-800'">{{ totalDuration }} minutes</span>
              </div>

              <div class="flex justify-between items-center text-zinc-400">
                <span class="font-normal">Acompte requis (15%)</span>
                <span class="font-bold text-amber-500">{{ requiredDeposit }} €</span>
              </div>

              <div class="flex justify-between items-center pt-2.5 border-t font-semibold text-sm transition-colors duration-200"
                :class="isDark ? 'border-zinc-900' : 'border-zinc-100'">
                <span :class="isDark ? 'text-white' : 'text-zinc-900'">Total à régler</span>
                <span class="text-base font-bold transition-colors duration-200" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ totalPrice }} €</span>
              </div>
            </div>
          </div>

          <!-- Notes et validation forcée -->
          <div class="pt-4 border-t space-y-4 transition-colors duration-200" :class="isDark ? 'border-zinc-900' : 'border-zinc-100'">
            <div class="space-y-2">
              <label class="block text-[11px] font-semibold" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Notes & Consignes de coiffure</label>
              <textarea v-model="form.notes" rows="3" placeholder="Préférences, nuances choisies ou antécédents capillaires du client..." class="w-full px-4 py-2.5 rounded-xl border text-xs font-medium transition-all resize-none focus:outline-none"
                :class="isDark ? 'border-zinc-900 bg-zinc-900/40 text-white placeholder-zinc-700 focus:border-zinc-700 focus:bg-zinc-900/80' : 'border-zinc-200 bg-zinc-50 text-zinc-900 placeholder-zinc-400 focus:border-zinc-300 focus:bg-white'"></textarea>
            </div>

            <div class="flex items-center justify-between p-3 rounded-xl border transition-colors duration-200"
              :class="isDark ? 'bg-zinc-950 border-zinc-900' : 'bg-zinc-50 border-zinc-200/60'">
              <span class="text-xs font-semibold" :class="isDark ? 'text-zinc-400' : 'text-zinc-600'">Marquer directement comme "Validé"</span>
              <input v-model="form.forceValidated" type="checkbox" class="h-4 w-4 rounded cursor-pointer transition-colors"
                :class="isDark ? 'border-zinc-800 bg-zinc-900 text-white accent-zinc-200' : 'border-zinc-300 bg-white text-zinc-900 accent-zinc-900'" />
            </div>
          </div>

          <!-- Bouton de confirmation principal -->
          <button @click="createAppointment" :disabled="isSubmitting" class="w-full py-3 rounded-xl text-xs font-semibold transition-all shadow-sm active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus:outline-none"
            :class="isDark 
              ? 'bg-white hover:bg-zinc-200 text-black' 
              : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
            {{ isSubmitting ? 'Enregistrement...' : 'Confirmer et planifier' }}
          </button>
        </div>

      </div>

    </div>

  </div>
</template>
<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import api from '@/api'

const router = useRouter()
const route = useRoute()
const isLoading = ref(true)
const isSubmitting = ref(false)
const catalog = ref([])
const staff = ref([])
const clientSearchResults = ref([])
const selectedClient = ref(null)
const searchTimeout = ref(null)
import { useTheme } from '@/composables/useTheme';

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();

// Vérifier si c'est un rendez-vous d'accompagnement
const isAccompagnement = computed(() => route.query.type === 'accompagnement')

// Main reactive storage form block
const form = reactive({
  clientSearch: '',
  selectedServiceIds: [],
  employeId: '',
  date: '',
  startTime: '',
  notes: '',
  forceValidated: false,
  nomComplet: '',
  email: '',
  telephone: ''
})

// Fetch initial data on mount
onMounted(async () => {
  await Promise.all([fetchServices(), fetchStaff()])

  // Pré-remplir le formulaire avec les query params du calendrier
  const hasPrefilledData = route.query.date || route.query.time || route.query.employeeId

  if (route.query.date) {
    form.date = route.query.date
    console.log('📅 Date pré-remplie:', form.date)
  }

  if (route.query.time) {
    form.startTime = route.query.time
    console.log('⏰ Heure pré-remplie:', form.startTime)
  }

  if (route.query.employeeId) {
    form.employeId = route.query.employeeId
    console.log('👤 Employé pré-sélectionné:', form.employeId)
  }

  // Pré-remplir les données du rendez-vous si ID fourni
  if (route.query.id) {
    try {
      const id = route.query.id
      console.log('🔍 Chargement du rendez-vous:', id)

      // Essayer de charger comme rendez-vous
      try {
        const response = await api.appointments.getAppointmentById(id)
        if (response.data) {
          const appointment = response.data
          console.log('✅ Rendez-vous chargé:', appointment)

          // Pré-remplir le client
          if (appointment.client) {
            selectedClient.value = {
              clientId: appointment.client._id || appointment.client.clientId,
              nom: appointment.nom || appointment.client.nom || '',
              prenom: appointment.prenom || appointment.client.prenom || '',
              email: appointment.email || appointment.client.email || '',
              telephone: appointment.telephone || appointment.client.telephone || ''
            }
            form.clientSearch = `${selectedClient.value.prenom} ${selectedClient.value.nom} - ${selectedClient.value.email}`
          } else {
            // Si pas de client lié, utiliser les données directes
            form.nomComplet = appointment.nom || ''
            form.email = appointment.email || ''
            form.telephone = appointment.telephone || ''
          }

          // Pré-remplir les services
          const services = appointment.service || appointment.services || []
          form.selectedServiceIds = services.map(s => s._id || s).filter(Boolean)
          console.log('🔧 Services pré-remplis:', form.selectedServiceIds)

          // Pré-remplir la date et l'heure
          if (appointment.start) {
            const startDate = new Date(appointment.start)
            form.date = startDate.toISOString().split('T')[0]
            form.startTime = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`
          } else if (appointment.date_rdv) {
            const startDate = new Date(appointment.date_rdv)
            form.date = startDate.toISOString().split('T')[0]
            form.startTime = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`
          }

          // Pré-remplir l'employé
          if (appointment.employe?._id) {
            form.employeId = appointment.employe._id
          } else if (appointment.employeId) {
            form.employeId = appointment.employeId
          }

          // Pré-remplir les notes
          form.notes = appointment.notes || ''

          toast.success('Rendez-vous chargé avec succès', {
            position: 'top-right',
            autoClose: 2500
          })
        }
      } catch (appointmentError) {
        // Si ce n'est pas un rendez-vous, essayer de charger comme client
        console.log('⚠️ Pas un rendez-vous, essai comme client...')
        const response = await api.clients.getClientById(id)
        if (response.data) {
          const client = response.data
          selectedClient.value = {
            clientId: client._id || client.clientId,
            nom: client.nom || '',
            prenom: client.prenom || '',
            email: client.email || '',
            telephone: client.telephone || ''
          }
          form.clientSearch = `${client.prenom} ${client.nom} - ${client.email}`
          console.log('✅ Client pré-rempli:', client.prenom, client.nom)
          toast.success(`Client sélectionné: ${client.prenom} ${client.nom}`, {
            position: 'top-right',
            autoClose: 2500
          })
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement:', error)
      toast.error('Erreur lors du chargement des données', {
        position: 'top-right',
        autoClose: 2500
      })
    }
  }

  // Afficher un message si des données ont été pré-remplies
  if (hasPrefilledData && !route.query.id) {
    toast.info('Date et heure pré-remplies depuis le calendrier', {
      position: 'top-right',
      autoClose: 2500
    })
  }

  // Afficher un message spécial pour les rendez-vous d'accompagnement
  if (route.query.type === 'accompagnement') {
    toast.success('Bienvenue ! Planifiez votre session d\'accompagnement personnalisé', {
      position: 'top-right',
      autoClose: 4000
    })
  }

  isLoading.value = false
})

// Fetch services
const fetchServices = async () => {
  try {
    const response = await api.services.getAllServices()
    console.log('📋 Réponse services complète:', response)
    console.log('📋 response.data:', response.data)

    catalog.value = response.data || []
    console.log('✅ Services chargés:', catalog.value.length, 'prestations')

    if (catalog.value.length === 0) {
      console.warn('⚠️ Aucun service trouvé dans la réponse')
    }
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des services:', error)
    console.error('❌ Détails erreur:', error.response?.data)
    toast.error('Erreur lors du chargement des services', {
      position: 'top-right',
      autoClose: 3000
    })
  }
}

// Fetch staff
const fetchStaff = async () => {
  try {
    const response = await api.staff.getAllStaff()
    console.log('📊 Réponse staff complète:', response)
    console.log('📊 response.data:', response.data)
    console.log('📊 response.data.administrateurs:', response.data?.administrateurs)

    staff.value = response.data?.administrateurs || []
    console.log('✅ Staff chargé:', staff.value.length, 'employés')

    if (staff.value.length === 0) {
      console.warn('⚠️ Aucun employé trouvé dans la réponse')
    }
  } catch (error) {
    console.error('❌ Erreur lors de la récupération du personnel:', error)
    console.error('❌ Détails erreur:', error.response?.data)
    toast.error('Erreur lors du chargement du personnel', {
      position: 'top-right',
      autoClose: 3000
    })
  }
}

// Search clients with debounce
const searchClients = () => {
  clearTimeout(searchTimeout.value)

  if (!form.clientSearch || form.clientSearch.length < 2) {
    clientSearchResults.value = []
    return
  }

  searchTimeout.value = setTimeout(async () => {
    try {
      const response = await api.clients.getAllClients({ search: form.clientSearch })
      clientSearchResults.value = response.data || []
    } catch (error) {
      console.error('Erreur lors de la recherche de clients:', error)
    }
  }, 300)
}

// Select a client from search results
const selectClient = (client) => {
  selectedClient.value = client
  form.clientSearch = `${client.nom} ${client.prenom || ''}`
  clientSearchResults.value = []
}

// Clear selected client
const clearSelectedClient = () => {
  selectedClient.value = null
  form.clientSearch = ''
}

// Services mapping layer logic
const selectedServices = computed(() => {
  return catalog.value.filter(s => form.selectedServiceIds.includes(s._id))
})

const totalPrice = computed(() => {
  return selectedServices.value.reduce((acc, curr) => acc + curr.prix, 0)
})

const totalDuration = computed(() => {
  return selectedServices.value.reduce((acc, curr) => acc + curr.duree, 0)
})

const requiredDeposit = computed(() => {
  return (totalPrice.value * 0.15).toFixed(2)
})

// Toggle arrays mechanisms
const toggleService = (service) => {
  const index = form.selectedServiceIds.indexOf(service._id)
  if (index === -1) {
    form.selectedServiceIds.push(service._id)
  } else {
    form.selectedServiceIds.splice(index, 1)
  }
}

const isSelected = (service) => {
  return form.selectedServiceIds.includes(service._id)
}

// Logic flow submission handlers
const resetForm = () => {
  form.clientSearch = ''
  form.selectedServiceIds = []
  form.employeId = ''
  form.date = ''
  form.startTime = ''
  form.notes = ''
  form.forceValidated = false
  form.nomComplet = ''
  form.email = ''
  form.telephone = ''
  selectedClient.value = null
  clientSearchResults.value = []
}

const createAppointment = async () => {
  if (isSubmitting.value) return

  // Validation
  if (!selectedClient.value && !form.nomComplet) {
    toast.error('Veuillez sélectionner un client ou saisir un nom', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  if (form.selectedServiceIds.length === 0) {
    toast.error('Veuillez sélectionner au moins un service', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  if (!form.employeId) {
    toast.error('Veuillez sélectionner un opérateur', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  if (!form.date) {
    toast.error('Veuillez sélectionner une date', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  if (!form.startTime) {
    toast.error('Veuillez sélectionner une heure de début', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  try {
    isSubmitting.value = true

    const payload = {
      employeId: form.employeId,
      serviceIds: form.selectedServiceIds,
      date: form.date,
      heureDebut: form.startTime,
      dureeTotale: totalDuration.value,
      clientId: selectedClient.value?.clientId || null,
      nomComplet: selectedClient.value ? `${selectedClient.value.nom} ${selectedClient.value.prenom || ''}` : form.nomComplet,
      email: selectedClient.value?.email || form.email || null,
      telephone: selectedClient.value?.telephone || form.telephone || null
    }

    await api.appointments.createAppointment(payload)

    toast.success('Rendez-vous créé avec succès !', {
      position: 'top-right',
      autoClose: 2000
    })

    // Redirect to appointments list
    setTimeout(() => {
      router.push('/appointments/list')
    }, 500)

  } catch (error) {
    console.error('Erreur lors de la création du rendez-vous:', error)
    toast.error(
      error.response?.data?.message || 'Erreur lors de la création du rendez-vous',
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
/* Technical override for beautiful inner overflow controls */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
